import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  SpecialtyDto,
  SpecialtyPayloadVM,
} from "../../types/specialty.definition";

interface SpecialtyState {
  specialties: SpecialtyDto[];
  loadedLang: string | null;
  loading: boolean;
  saving: boolean;
  deleting: boolean;
  error: string | null;
}

const initialState: SpecialtyState = {
  specialties: [],
  loadedLang: null,
  loading: false,
  saving: false,
  deleting: false,
  error: null,
};

// Actions
export const fetchSpecialtiesRequest = createAction<{ lang?: string }>(
  "specialties/fetchSpecialtiesRequest"
);

export const fetchSpecialtiesSuccess = createAction<{
  data: SpecialtyDto[];
  lang: string;
}>("specialties/fetchSpecialtiesSuccess");

export const fetchSpecialtiesFailure = createAction<string>(
  "specialties/fetchSpecialtiesFailure"
);

export const createSpecialtyRequest = createAction<{
  data: SpecialtyPayloadVM;
  onSuccess?: () => void;
  onError?: () => void;
}>("specialties/createSpecialtyRequest");

export const createSpecialtySuccess = createAction(
  "specialties/createSpecialtySuccess"
);

export const createSpecialtyFailure = createAction<string>(
  "specialties/createSpecialtyFailure"
);

export const updateSpecialtyRequest = createAction<{
  id: number;
  data: SpecialtyPayloadVM;
  onSuccess?: () => void;
  onError?: () => void;
}>("specialties/updateSpecialtyRequest");

export const updateSpecialtySuccess = createAction<number>(
  "specialties/updateSpecialtySuccess"
);

export const updateSpecialtyFailure = createAction<string>(
  "specialties/updateSpecialtyFailure"
);

export const deleteSpecialtyRequest = createAction<{
  id: number;
  onSuccess?: () => void;
  onError?: (code: string) => void;
}>("specialties/deleteSpecialtyRequest");

export const deleteSpecialtySuccess = createAction<number>(
  "specialties/deleteSpecialtySuccess"
);

export const deleteSpecialtyFailure = createAction<string>(
  "specialties/deleteSpecialtyFailure"
);

// Slice
const specialtySlice = createSlice({
  name: "specialties",
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    // Fetch
    builder
      .addCase(fetchSpecialtiesRequest, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchSpecialtiesSuccess,
        (
          state,
          action: PayloadAction<{ data: SpecialtyDto[]; lang: string }>
        ) => {
          state.loading = false;
          state.specialties = action.payload.data;
          state.loadedLang = action.payload.lang;
          state.error = null;
        }
      )
      .addCase(
        fetchSpecialtiesFailure,
        (state, action: PayloadAction<string>) => {
          state.loading = false;
          state.error = action.payload;
        }
      );

    // Create
    builder
      .addCase(createSpecialtyRequest, state => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createSpecialtySuccess, state => {
        state.saving = false;
        state.error = null;
      })
      .addCase(
        createSpecialtyFailure,
        (state, action: PayloadAction<string>) => {
          state.saving = false;
          state.error = action.payload;
        }
      );

    // Update
    builder
      .addCase(updateSpecialtyRequest, state => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateSpecialtySuccess, state => {
        state.saving = false;
        state.error = null;
      })
      .addCase(
        updateSpecialtyFailure,
        (state, action: PayloadAction<string>) => {
          state.saving = false;
          state.error = action.payload;
        }
      );

    // Delete
    builder
      .addCase(deleteSpecialtyRequest, state => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(
        deleteSpecialtySuccess,
        (state, action: PayloadAction<number>) => {
          state.deleting = false;
          state.specialties = state.specialties.filter(
            s => s.id !== action.payload
          );
          state.error = null;
        }
      )
      .addCase(
        deleteSpecialtyFailure,
        (state, action: PayloadAction<string>) => {
          state.deleting = false;
          state.error = action.payload;
        }
      );
  },
});

export const { clearError } = specialtySlice.actions;

// Selectors
export const selectSpecialties = (state: { specialties: SpecialtyState }) =>
  state.specialties.specialties;

export const selectSpecialtiesLoadedLang = (state: {
  specialties: SpecialtyState;
}) => state.specialties.loadedLang;

export const selectSpecialtiesLoading = (state: {
  specialties: SpecialtyState;
}) => state.specialties.loading;

export const selectSpecialtiesSaving = (state: {
  specialties: SpecialtyState;
}) => state.specialties.saving;

export const selectSpecialtiesDeleting = (state: {
  specialties: SpecialtyState;
}) => state.specialties.deleting;

export const selectSpecialtiesError = (state: {
  specialties: SpecialtyState;
}) => state.specialties.error;

export default specialtySlice.reducer;
